/**
 * TaskLibraryContext — top-level home for the Tasks panel (sub-plan 003, task 001).
 *
 * Follows the `agentLibContext.tsx` pattern (lead ruling #10): a top-level
 * provider is the single place to open/act on a library from anywhere. It owns:
 *   - the panel-open state (drives the left nav panel),
 *   - the three filter values (workspace / assignee / due) plus the ordering
 *     mode and the multi-select status filter, session-persisted per lead
 *     ruling #9.
 *
 * Two entry points share this one filter model:
 *   - the nav "Tasks" icon → `openTasksPanel()` — opens without touching filters.
 *   - the contextual chat-bar Tasks button → `openTasksPanel({ workspaceId })` —
 *     sets the workspace filter to the current workspace.
 *
 * The `TasksPanel` component consumes `useTaskLibrary()` instead of owning filter
 * state locally, so both entries (and any future host) share one model.
 */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";
import { useNav } from "@/chat2/Chat2Provider";
import { useSessionState } from "@/hooks/useSessionState";
import { TASK_STATUSES, type TaskOrderMode, type TaskStatus } from "@bloomstack/apis/services/taskService";
import type { DueFilterState } from "@/chat2/components/navPanels/tasks/dueFilterLogic";
import type { OrderDirection } from "@/chat2/components/navPanels/tasks/taskGrouping";

export type TaskLibraryContextType = {
  /** Whether the Tasks panel is open. */
  isOpen: boolean;
  /** The persisted workspace filter (`null` = "All workspaces"). */
  workspaceFilter: string | null;
  setWorkspaceFilter: (workspaceId: string | null) => void;
  /** The persisted assignee filter (profile id, `null` = anyone). */
  assigneeFilter: string | null;
  setAssigneeFilter: (assignedProfileId: string | null) => void;
  /** The persisted due filter (`null` = All). */
  dueFilter: DueFilterState | null;
  setDueFilter: (due: DueFilterState | null) => void;
  /** The persisted ordering mode (`"execution"` by default). */
  orderMode: TaskOrderMode;
  setOrderMode: (mode: TaskOrderMode) => void;
  /**
   * The persisted reading direction (ascending by default). Independent of
   * `orderMode`: the mode picks WHICH date, the direction picks which way.
   */
  orderDirection: OrderDirection;
  toggleOrderDirection: () => void;
  /** The persisted multi-select status filter (`null` = show all). */
  statusFilter: TaskStatus[] | null;
  setStatusFilter: (statuses: TaskStatus[] | null) => void;
  /**
   * Open the Tasks panel. When `workspaceId` is provided (the contextual
   * entry's contract) the workspace filter is set to it; when absent (the nav
   * icon's contract) nothing is touched — repeated clicks never reset filters.
   */
  openTasksPanel: (opts?: { workspaceId?: string | null }) => void;
  /** Close the Tasks panel (does not alter filters). */
  closeTasksPanel: () => void;
  /**
   * A task that an outside driver asked to open (the notification panel's button).
   * `TasksPanel` consumes it and clears it; it lives here because the request has
   * to survive until that panel is mounted and its mirror has the row.
   */
  pendingTaskId: string | null;
  /**
   * Open the Tasks panel AND one task's detail form — the navigation driver entry
   * for notifications. Same filter contract as `openTasksPanel`.
   */
  openTask: (taskId: string, opts?: { workspaceId?: string | null }) => void;
  /** Clear the pending request once the panel has opened it. */
  clearPendingTask: () => void;
};

export const TaskLibraryContext = createContext<TaskLibraryContextType | undefined>(undefined);

export const useTaskLibrary = () => {
  const context = useContext(TaskLibraryContext);
  if (!context) {
    throw new Error("useTaskLibrary must be used within a TaskLibraryProvider");
  }
  return context;
};

export const TaskLibraryProvider = ({ children }: { children: ReactNode }) => {
  const nav = useNav(false);

  // Session-persisted filters (lead ruling #9) — survive panel close/reopen and
  // full page reloads (localStorage-backed, per-user scope).
  const [workspaceFilter, setWorkspaceFilter] = useSessionState<string | null>("tasks:workspaceFilter", null);
  const [assigneeFilter, setAssigneeFilter] = useSessionState<string | null>("tasks:assigneeFilter", null);
  const [dueFilter, setDueFilter] = useSessionState<DueFilterState | null>("tasks:dueFilter", null);
  const [storedOrderMode, setOrderMode] = useSessionState<TaskOrderMode>("tasks:orderMode", "execution");
  const [storedOrderDirection, setOrderDirection] = useSessionState<OrderDirection>("tasks:orderDirection", "asc");
  const [storedStatusFilter, setStatusFilter] = useSessionState<TaskStatus[] | null>("tasks:statusFilter", null);

  // Normalize on read: a corrupt/stale localStorage value must not blank the
  // panel behind a server `INTERNAL` error. Setters only ever write valid
  // values, so only the exposed values are normalized.
  const orderMode: TaskOrderMode = storedOrderMode === "due" ? "due" : "execution";
  const orderDirection: OrderDirection = storedOrderDirection === "desc" ? "desc" : "asc";
  // A toggle rather than a setter: there are exactly two directions, and the
  // control is a single button that flips.
  const toggleOrderDirection = useCallback(() => {
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  }, [orderDirection, setOrderDirection]);
  const statusFilter: TaskStatus[] | null = (() => {
    if (storedStatusFilter === null) return null;
    const valid = storedStatusFilter.filter((s) => TASK_STATUSES.includes(s));
    return valid.length > 0 ? valid : null;
  })();

  const openTasksPanel = useCallback((opts?: { workspaceId?: string | null }) => {
    // Contextual entry: honour the provided workspace. Note this is the ONLY
    // place the workspace filter is written from an entry point — the nav icon
    // calls with no opts and writes nothing.
    if (opts && "workspaceId" in opts) {
      setWorkspaceFilter(opts.workspaceId ?? null);
    }

    // Drive the left nav panel (the Tasks panel is a left nav panel, not a tab).
    nav?.setActive("tasks");
    nav?.show(true);
  }, [nav, setWorkspaceFilter]);

  const closeTasksPanel = useCallback(() => {
    nav?.show(false);
  }, [nav]);

  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);

  const openTask = useCallback((taskId: string, opts?: { workspaceId?: string | null }) => {
    // Same workspace-filter rule as `openTasksPanel`: only an explicit
    // `workspaceId` key writes the filter, so the notification's own workspace wins.
    if (opts && "workspaceId" in opts) {
      setWorkspaceFilter(opts.workspaceId ?? null);
    }
    setPendingTaskId(taskId);
    nav?.setActive("tasks");
    nav?.show(true);
  }, [nav, setWorkspaceFilter]);

  const clearPendingTask = useCallback(() => {
    setPendingTaskId(null);
  }, []);

  const api = useMemo<TaskLibraryContextType>(() => ({
    isOpen: !!nav?.displayToolPanel && nav?.currentPanel?.id === "tasks",
    workspaceFilter,
    setWorkspaceFilter,
    assigneeFilter,
    setAssigneeFilter,
    dueFilter,
    setDueFilter,
    orderMode,
    setOrderMode,
    orderDirection,
    toggleOrderDirection,
    statusFilter,
    setStatusFilter,
    openTasksPanel,
    closeTasksPanel,
    pendingTaskId,
    openTask,
    clearPendingTask,
  }), [
    nav?.displayToolPanel,
    nav?.currentPanel?.id,
    workspaceFilter,
    setWorkspaceFilter,
    assigneeFilter,
    setAssigneeFilter,
    dueFilter,
    setDueFilter,
    orderMode,
    setOrderMode,
    orderDirection,
    toggleOrderDirection,
    statusFilter,
    setStatusFilter,
    openTasksPanel,
    closeTasksPanel,
    pendingTaskId,
    openTask,
    clearPendingTask,
  ]);

  return (
    <TaskLibraryContext.Provider value={api}>
      {children}
    </TaskLibraryContext.Provider>
  );
};
